import {
  AlignmentType,
  Document,
  Header,
  ISectionOptions,
  ImageRun,
  NumberFormat,
  Packer,
  PageBreak,
  PageNumber,
  Paragraph,
  SectionType,
  ShadingType,
  TextRun,
} from "docx";
import { saveAs } from "file-saver";
import { Checklist, ChecklistCriteria } from "types/Checklist";
import { Revision } from "types/Revision";
import { SubmittedDocument } from "types/SubmittedDocument";
import { WordChecklistCells } from "types/WordChecklist";
import { RevisionCommentPlacement } from "~/types/enums/RevisionCommentPlacement.enum";
import {
  ExportBlock,
  ExportInlineRun,
  useQuillOpsToExportBlocks,
} from "../quill-export/useQuillOpsToExportBlocks";
import { useWordChecklistGenerator } from "./useWordChecklistGenerator";
import { useWordPageSize } from "./useWordPageSize";

const toWordAlignment = (align?: string) => {
  switch (align) {
    case "center":
      return AlignmentType.CENTER;
    case "right":
      return AlignmentType.RIGHT;
    case "justify":
      return AlignmentType.JUSTIFIED;
    default:
      return AlignmentType.LEFT;
  }
};

const toTwipsFromLineHeight = (lineHeight?: number) => {
  const normalized = Number.isFinite(lineHeight) ? Number(lineHeight) : 1;
  return Math.round(normalized * 240);
};

const toTwipsFromIndentLevel = (indentLevel?: number) => {
  const normalized = Number.isFinite(indentLevel) ? Number(indentLevel) : 0;
  return Math.round(normalized * 360);
};

const toTwipsFromEm = (em: number) => {
  // 1em at 12pt ~= 12pt; 1pt = 20 twips
  return Math.round(em * 12 * 20);
};

const normalizeColor = (color?: string) => {
  if (!color) return undefined;

  const raw = String(color).trim().toLowerCase();
  const namedColorMap: Record<string, string> = {
    red: "ff0000",
    blue: "0000ff",
    green: "008000",
    black: "000000",
    white: "ffffff",
    yellow: "ffff00",
    gray: "808080",
    grey: "808080",
  };

  if (namedColorMap[raw]) {
    return namedColorMap[raw];
  }

  if (/^#[0-9a-f]{6}$/i.test(raw)) {
    return raw.replace("#", "");
  }

  if (/^[0-9a-f]{6}$/i.test(raw)) {
    return raw;
  }

  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    const hex = raw.replace("#", "");
    return `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const rgbMatch = raw.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)$/
  );
  if (rgbMatch) {
    const toHex = (value: string) => {
      const num = Math.max(0, Math.min(255, Number(value)));
      return num.toString(16).padStart(2, "0");
    };
    return `${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`;
  }

  // Unknown format: omit color to avoid docx hex validation errors.
  return undefined;
};

const toPtNumber = (width?: string): number | undefined => {
  if (!width) return undefined;
  const raw = String(width).trim().toLowerCase();
  if (raw.endsWith("pt")) {
    const numeric = Number(raw.replace("pt", ""));
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  if (raw.endsWith("em")) {
    const numeric = Number(raw.replace("em", ""));
    return Number.isFinite(numeric) ? numeric * 12 : undefined;
  }
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const createTextRun = (run: ExportInlineRun) => {
  const textRunOptions: any = {
    text: run.text,
    bold: run.bold || false,
    italics: run.italic || false,
    underline: run.underline ? {} : undefined,
    strike: run.strike || false,
    color: normalizeColor(run.color),
    size:
      run.fontSizePt != undefined && Number.isFinite(run.fontSizePt)
        ? Math.round(run.fontSizePt * 2)
        : undefined,
    shading: run.background
      ? {
          type: ShadingType.CLEAR,
          fill: normalizeColor(run.background),
          color: "FFFFFF",
        }
      : undefined,
    subScript: run.script === "sub" ? true : undefined,
    superScript: run.script === "super" ? true : undefined,
  };

  return new TextRun(textRunOptions);
};

const extractHeaderConfig = (revision: Revision) => {
  const header = revision.headers?.[0];
  if (!header) return undefined;

  if (
    (header.extraText == undefined || header.extraText.trim() === "") &&
    header.includePageNumbers != true
  ) {
    return undefined;
  }

  return header;
};

const createSectionHeader = (revision: Revision) => {
  const header = extractHeaderConfig(revision);
  if (!header) return undefined;

  const children: TextRun[] = [];
  const extraText = header.extraText?.trim();

  if (extraText) {
    children.push(new TextRun(extraText));
  }

  if (header.includePageNumbers) {
    if (children.length > 0) {
      children.push(new TextRun(" "));
    }
    children.push(new TextRun({ children: [PageNumber.CURRENT] }));
  }

  return new Header({
    children: [
      new Paragraph({
        alignment: toWordAlignment(header.alignment),
        children,
      }),
    ],
  });
};

const appendRevisionComments = (
  paragraphs: Paragraph[],
  revision: Revision,
  commentColor: number[]
) => {
  if (!revision.commentObject) return;

  const commentLines = useConvertQuillDeltaToTextRuns(
    revision.commentObject,
    24,
    useRGBToHex(commentColor)
  );

  for (const line of commentLines) {
    paragraphs.push(
      new Paragraph({
        spacing: { line: 300 },
        children: line,
      })
    );
  }
};

const toImageRun = async (src: string) => {
  const response = await fetch(src);
  const blob = await response.blob();
  const { width: originalWidth, height: originalHeight } =
    await useImageDimensions(blob);
  const buffer = await blob.arrayBuffer();

  const width = 500;
  return new ImageRun({
    data: buffer,
    transformation: {
      width,
      height: Math.floor((originalHeight / originalWidth) * width),
    },
  });
};

const appendOpsBlocks = async (paragraphs: Paragraph[], blocks: ExportBlock[]) => {
  for (const block of blocks) {
    if (block.type === "page-break") {
      paragraphs.push(new Paragraph({ children: [new PageBreak()] }));
      continue;
    }

    if (block.type === "image" && block.imageSrc) {
      try {
        const imageRun = await toImageRun(block.imageSrc);
        paragraphs.push(
          new Paragraph({
            alignment: toWordAlignment(block.align),
            children: [imageRun, new TextRun(" ")],
          })
        );
        paragraphs.push(new Paragraph({}));
      } catch (error) {
        // Keep export resilient when an inline image cannot be fetched.
      }
      continue;
    }

    const runs = (block.runs ?? []).map((run) => createTextRun(run));
    const firstLineIndentPt = toPtNumber(block.firstLineIndentWidth);
    const leadingFirstLineIndentPt = toPtNumber(block.leadingFirstLineIndentWidth);
    const explicitFirstLineIndentTwips =
      firstLineIndentPt != undefined ? Math.round(firstLineIndentPt * 20) : 0;
    const leadingFirstLineIndentTwips =
      leadingFirstLineIndentPt != undefined
        ? Math.round(leadingFirstLineIndentPt * 20)
        : 0;
    const firstLineIndentTwips =
      block.hasHangingIndent != true &&
      (explicitFirstLineIndentTwips !== 0 || leadingFirstLineIndentTwips > 0)
        ? explicitFirstLineIndentTwips + leadingFirstLineIndentTwips
        : undefined;
    const baseLeftIndent = (() => {
      if (block.leftIndentWidth != undefined) {
        return Math.round((toPtNumber(block.leftIndentWidth) ?? 0) * 20);
      }
      return block.indentLevel && block.indentLevel > 0
        ? toTwipsFromIndentLevel(block.indentLevel)
        : 0;
    })();
    const hangingIndentTwips = block.hasHangingIndent
      ? toTwipsFromEm(2)
      : Math.max(0, -(firstLineIndentTwips ?? 0));

    const options: any = {
      children: runs.length > 0 ? runs : [new TextRun("")],
      alignment: toWordAlignment(block.align),
      spacing: {
        line: toTwipsFromLineHeight(block.lineHeight),
      },
      indent: {
        left:
          baseLeftIndent + (block.hasHangingIndent ? hangingIndentTwips : 0) > 0
            ? baseLeftIndent + (block.hasHangingIndent ? hangingIndentTwips : 0)
            : undefined,
        firstLine: firstLineIndentTwips != undefined && firstLineIndentTwips > 0
          ? firstLineIndentTwips : undefined,
        hanging: hangingIndentTwips > 0 ? hangingIndentTwips : undefined,
      },
    };

    if (block.headerLevel != undefined && block.headerLevel > 0) {
      options.heading = `Heading${Math.min(6, block.headerLevel)}`;
      options.spacing = {
        ...options.spacing,
        before: 120,
        after: 120,
      };
    }

    if (block.isBlockquote) {
      options.indent = {
        ...options.indent,
        left: (options.indent?.left ?? 0) + 360,
      };
    }

    if (block.isCodeBlock) {
      options.shading = {
        type: ShadingType.CLEAR,
        fill: "F5F5F5",
        color: "FFFFFF",
      };
    }

    if (block.listType) {
      options.numbering = {
        reference:
          block.listType === "ordered"
            ? "companion-ordered-list"
            : "companion-bullet-list",
        level: Math.max(0, Math.min(8, block.indentLevel ?? 0)),
      };
    }

    paragraphs.push(new Paragraph(options));
  }
};

export const useWordOpsDocumentGenerator = () => {
  const exportWordOpsDocument = async (
    submittedDocument: SubmittedDocument,
    revision: Revision,
    checklist: Checklist | undefined,
    allCriteria: ChecklistCriteria[] | undefined,
    wordChecklistCells: WordChecklistCells | undefined,
    totalEarnedScore: number | undefined,
    earnedScore: number | undefined,
    commentColor: number[],
    returnBlob: boolean = false,
    hideScores: boolean = false,
    fileNameOverride: string | undefined = undefined,
    includeCriteriaCheckmarks: boolean = false,
    isStudentMode: boolean = false,
    useGoogleDocsCompatibleChecklistLayout: boolean = false
  ) => {
    const pageSize = useWordPageSize();
    const sections: ISectionOptions[] = [];

    if (
      checklist &&
      wordChecklistCells &&
      allCriteria &&
      totalEarnedScore != undefined &&
      earnedScore != undefined
    ) {
      const checklistSections = useWordChecklistGenerator(
        submittedDocument,
        checklist,
        wordChecklistCells,
        allCriteria,
        totalEarnedScore,
        earnedScore,
        hideScores,
        includeCriteriaCheckmarks,
        isStudentMode,
        useGoogleDocsCompatibleChecklistLayout
      );
      sections.push(...checklistSections);
    }

    const assignmentParagraphs: Paragraph[] = [];
    const revisionCommentPlacement =
      revision.commentPlacement ?? RevisionCommentPlacement.AfterDocument;

    if (revisionCommentPlacement == RevisionCommentPlacement.BeforeDocument) {
      appendRevisionComments(assignmentParagraphs, revision, commentColor);
    }

    const blocks = useQuillOpsToExportBlocks(revision.studentSubmissionOps ?? []);
    await appendOpsBlocks(assignmentParagraphs, blocks);

    if (revisionCommentPlacement == RevisionCommentPlacement.AfterDocument) {
      appendRevisionComments(assignmentParagraphs, revision, commentColor);
    }

    const sectionHeader = createSectionHeader(revision);

    sections.push({
      properties: {
        page: {
          ...pageSize,
          // Checklist pages precede the composition but do not count toward it.
          pageNumbers: { start: 1 },
        },
        type: sections.length > 0 ? SectionType.NEXT_PAGE : undefined,
      },
      headers: sectionHeader ? { default: sectionHeader } : undefined,
      children: assignmentParagraphs,
    });

    const doc = new Document({
      sections,
      numbering: {
        config: [
          {
            reference: "companion-ordered-list",
            levels: Array.from({ length: 9 }, (_, level) => ({
              level,
              format: NumberFormat.DECIMAL,
              text: `%${level + 1}.`,
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: 720 + level * 360,
                    hanging: 260,
                  },
                },
              },
            })),
          },
          {
            reference: "companion-bullet-list",
            levels: Array.from({ length: 9 }, (_, level) => ({
              level,
              format: NumberFormat.BULLET,
              text: "•",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: 720 + level * 360,
                    hanging: 260,
                  },
                },
              },
            })),
          },
        ],
      },
      compatibility: {},
      styles: {
        default: {
          document: {
            run: {
              size: 24,
            },
          },
        },
      },
    });

    const blob = await Packer.toBlob(doc);
    if (returnBlob) return blob;

    let docName = fileNameOverride ?? submittedDocument.name;
    docName = docName.replace(".docx", "");
    docName = fileNameOverride == undefined ? `${docName}_graded.docx` : `${docName}.docx`;
    saveAs(blob, docName);
  };

  return { exportWordOpsDocument };
};
